import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

// Google OAuth implementation using Supabase

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isAuthenticated: boolean;
  signUp: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string, userEmail?: string, userMetadata?: any) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    
    if (!error && data) {
      setProfile(data);
      return data;
    }
    
    // If no profile exists, create one (fallback for users created before trigger)
    if (!data && !error) {
      // Extract name from metadata (Google OAuth provides full_name)
      let displayName = "User";
      if (userMetadata?.full_name) {
        displayName = userMetadata.full_name;
      } else if (userMetadata?.name) {
        displayName = userMetadata.name;
      } else if (userEmail) {
        displayName = userEmail.split("@")[0];
      }

      const { data: newProfile, error: insertError } = await supabase
        .from("profiles")
        .insert({
          id: userId,
          name: displayName,
          email: userEmail,
          // Optionally import Google profile picture
          photo_url: userMetadata?.avatar_url || userMetadata?.picture || null,
        })
        .select()
        .single();
      
      if (!insertError && newProfile) {
        setProfile(newProfile);
        return newProfile;
      }
    }
    
    return data;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id, user.email);
    }
  }, [user?.id, user?.email, fetchProfile]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);
        
        // Handle SIGNED_OUT event explicitly
        if (event === "SIGNED_OUT") {
          console.log("User signed out, clearing all state");
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          // Handle Google OAuth profile data
          const userMetadata = session.user.user_metadata || {};
          const appMetadata = session.user.app_metadata || {};
          
          // Check if user signed in with Google
          // Provider info is in app_metadata.provider or identities array
          const isGoogleSignIn = appMetadata.provider === "google" || 
                                 session.user.identities?.some((id: any) => id.provider === "google");
          
          console.log("Is Google sign-in:", isGoogleSignIn, {
            appMetadataProvider: appMetadata.provider,
            identities: session.user.identities,
            userMetadata
          });
          
          if (isGoogleSignIn && event === "SIGNED_IN") {
            console.log("Processing Google OAuth sign-in for user:", session.user.id);
            
            // Check if this is a new user or returning user
            const { data: existingProfile } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", session.user.id)
              .maybeSingle();

            if (existingProfile) {
              // Returning user - update profile with Google data if missing
              const updates: any = {
                last_login: new Date().toISOString(),
                provider: "google",
                email_verified: true,
              };
              if (!existingProfile.name && userMetadata.full_name) {
                updates.name = userMetadata.full_name;
              }
              if (!(existingProfile as any).email && session.user.email) {
                updates.email = session.user.email;
              }
              if (!existingProfile.photo_url && (userMetadata.avatar_url || userMetadata.picture)) {
                updates.photo_url = userMetadata.avatar_url || userMetadata.picture;
              }

              console.log("Updating profile with Google data:", updates);
              const { error: updateError } = await supabase
                .from("profiles")
                .update(updates)
                .eq("id", session.user.id);
              
              if (updateError) {
                console.error("Error updating profile:", updateError);
              }
              
              console.log("Returning user detected, profile exists:", existingProfile.photo_url ? "complete" : "incomplete");
            } else {
              // New user - profile will be created by trigger with provider=google
              console.log("New Google user detected, profile will be created by trigger");
            }
          }

          // Always fetch profile (trigger should create it if it doesn't exist)
          // Reduced delay to improve loading speed
          setTimeout(() => {
            fetchProfile(session.user.id, session.user.email, userMetadata);
          }, 50);
        } else {
          setProfile(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        const userMetadata = session.user.user_metadata || {};
        fetchProfile(session.user.id, session.user.email, userMetadata);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signUp = useCallback(async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { data, error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Update last_login and email_verified on successful sign-in
    if (!error && data?.user) {
      await (supabase.from("profiles") as any)
        .update({
          last_login: new Date().toISOString(),
          email_verified: true,
        })
        .eq("id", data.user.id);
    }
    
    return { data, error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      console.log("Initiating Google OAuth sign-in...");
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });
      
      if (error) {
        console.error("Google OAuth error:", error);
        return { error };
      }
      
      // Supabase should automatically redirect, but if we get a URL, log it
      if (data?.url) {
        console.log("OAuth URL received, redirecting:", data.url);
        // Supabase handles the redirect automatically, but we can verify
      }
      
      // If no error and no URL, Supabase is handling redirect automatically
      return { error: null };
    } catch (err: any) {
      console.error("Unexpected error in signInWithGoogle:", err);
      return { error: err };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Sign out error:", error);
        return { error };
      }
      
      // Clear all state immediately
      setSession(null);
      setUser(null);
      setProfile(null);
      setLoading(false);
      
      // Clear any cached data
      localStorage.removeItem('app_language'); // Keep language preference
      
      console.log("User signed out successfully");
      return { error: null };
    } catch (err) {
      console.error("Sign out exception:", err);
      return { error: err };
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        isAuthenticated: !!session,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
